/**
 * Computes the display date range (startDate/endDate as ISO date strings) for a given
 * anchor date + granularity. Shared between the ResourceUtilizationChart component
 * (period label display) and its hook (comparison-period range computation) so the
 * logic isn't duplicated within the tile.
 *
 * @param {string} dateStr - Anchor date (YYYY-MM-DD).
 * @param {string} gran - One of "day", "week", "month", "year".
 * @returns {{ startDate: string, endDate: string }}
 */
export function computeDisplayRange(dateStr, gran) {
  if (!dateStr) return { startDate: "", endDate: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { startDate: "", endDate: "" };
  const fmt = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

  if (gran === "day") return { startDate: fmt(d), endDate: fmt(d) };

  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmt(mon), endDate: fmt(sun) };
  }

  if (gran === "month") {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { startDate: fmt(start), endDate: fmt(end) };
  }

  // year
  return {
    startDate: `${d.getFullYear()}-01-01`,
    endDate: `${d.getFullYear()}-12-31`,
  };
}

const GERMAN_MONTH_ABBR = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function toGermanDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/**
 * Short, human-readable comparison-period label, scaled to granularity.
 * Mirrored byte-for-byte in backend/src/shared/cache_warming.py's
 * _format_comparison_period_label — period_label is part of the outer
 * resource-utilization-chart cache key, so the two must never drift.
 */
export function formatComparisonPeriodLabel(startDate, endDate, granularity) {
  if (!startDate) return "";
  if (granularity === "year") return startDate.slice(0, 4);
  if (granularity === "month") {
    const d = new Date(startDate + "T00:00:00");
    if (Number.isNaN(d.getTime())) return startDate;
    return `${GERMAN_MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`;
  }
  if (startDate === endDate) return toGermanDate(startDate);
  return `${toGermanDate(startDate)} – ${toGermanDate(endDate)}`;
}
